import {
  SkyBadge,
  SkyDataGrid,
  SkyDialog,
  SkyMessageBar,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState } from "react";
import { CONNECTOR_HUB_VALIDATORS } from "@shared/connectors/hubValidators";
import { seedErrors, seedWarnings, validateSeed } from "@shared/connectors/seedValidator";
import type {
  ConnectorAdminView,
  ConnectorSeedDocument,
  ConnectorUpsertResult,
  SeedWarning,
} from "@shared/types/connectorsAdmin";
import { ApiClientError, referenceIdOf, referenceIdToShow } from "@/lib/api";
import {
  documentCarriesUncomparedBlocks,
  seedDrift,
  type DriftRow,
} from "@/components/platform/seedDrift";
import { readTextFile } from "@/components/platform/readTextFile";
import { ErrorState } from "@/components/platform/ScreenState";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Upload a connector seed document (FR-30, FR-31, canvas 13 panel 05).
 *
 * The rule the whole dialog exists to enforce: **nothing is applied until it is
 * asked for a second time**. Choosing a file posts it with `dryRun: true`,
 * which classifies without writing, and only the explicit Apply posts it again
 * for real. That is why the bar at the top of the result says "Nothing has been
 * applied" before it says anything else — a reader who stops reading there has
 * still learned the one fact that matters.
 *
 * File upload is the documented skypoint-ui gap (GUIDELINES §3.1), so this is a
 * native `<input type="file">` wearing the design system's own tokens, with a
 * visually hidden label so it is named in the accessibility tree.
 *
 * A13: before anything is sent, the file runs through `validateSeed`, the same
 * validator the upload route, the seed CLI and the catalog test use. An error
 * keeps the file here, lists each finding with its path into the document, and
 * sends nothing; warnings are listed on the dry-run step next to the result.
 * The route runs the same check again, so a 400 it answers is listed the same way.
 */

interface DryRunState {
  seed: ConnectorSeedDocument;
  fileName: string;
  uploadedAt: string;
  result: ConnectorUpsertResult;
}

/** One finding as a line: its path into the document, then what is wrong. */
interface FindingLine {
  field: string;
  issue: string;
}

/** Past this many lines the list says how many more there are rather than scrolling forever. */
const MAX_LINES = 20;

function FindingList({ lines, testId }: { lines: FindingLine[]; testId: string }) {
  const shown = lines.slice(0, MAX_LINES);
  return (
    <ul className="mt-1 flex list-disc flex-col gap-1 pl-5" data-testid={testId}>
      {shown.map((line, index) => (
        <li key={`${line.field}-${index}`}>
          <span className="font-mono">{line.field}</span>: {line.issue}
        </li>
      ))}
      {lines.length > shown.length ? <li>{`and ${lines.length - shown.length} more`}</li> : null}
    </ul>
  );
}

function driftColor(kind: DriftRow["kind"]): "success" | "danger" | "warning" {
  if (kind === "Added") return "success";
  return kind === "Removed" ? "danger" : "warning";
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** A refusal the route answered: its details carry `seed.<path>`, listed like the browser's own. */
function serverFindingsOf(shownError: unknown): FindingLine[] {
  if (!(shownError instanceof ApiClientError) || shownError.code !== "VALIDATION_FAILED") return [];
  return shownError.details.map((detail) => ({
    field: detail.field ?? "seed",
    issue: detail.issue,
  }));
}

function applyLabel(dryRun: DryRunState): string {
  return dryRun.result.action === "insert"
    ? `Create ${dryRun.seed.name}`
    : `Apply to ${dryRun.seed.name}`;
}

function dryRunHeading(dryRun: DryRunState): string {
  if (dryRun.result.action === "insert") return `${dryRun.seed.name} is not in this deployment yet`;
  if (dryRun.result.action === "skip_no_changes") {
    return `The file for ${dryRun.seed.name} matches the stored definition`;
  }
  return `The file for ${dryRun.seed.name} differs from the stored definition`;
}

/** The refusal of the file that was sent, the browser's own findings, and the warnings. */
function SeedFindings({
  shownError,
  fileError,
  fileErrors,
  serverFindings,
  visibleWarnings,
}: {
  shownError: unknown;
  fileError: string | null;
  fileErrors: FindingLine[];
  serverFindings: FindingLine[];
  visibleWarnings: FindingLine[];
}) {
  const refusedReference =
    shownError instanceof Error
      ? referenceIdToShow(shownError.message, referenceIdOf(shownError))
      : null;
  return (
    <>
      {/* A request error belongs to the file that was SENT; a newer file refused here in
        the browser was never sent, so the older refusal would only mislead. */}
      {shownError && !fileError && fileErrors.length === 0 && serverFindings.length === 0 ? (
        <ErrorState title="That file was refused" error={shownError} />
      ) : null}
      {/* The route's findings go INSIDE the alert, so a screen reader announces the paths
        with the title, not only the title (review r1). */}
      {shownError && serverFindings.length > 0 && !fileError && fileErrors.length === 0 ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="That file was refused"
          message={
            <div>
              <span>
                {shownError instanceof Error ? shownError.message : "Something went wrong."}
                {refusedReference ? (
                  <>
                    {" "}
                    Reference id <span className="font-mono">{refusedReference}</span>.
                  </>
                ) : null}
              </span>
              <FindingList lines={serverFindings} testId="seed-server-findings" />
            </div>
          }
          testId="seed-server-error"
        />
      ) : null}
      {fileError ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="That file could not be read"
          message={fileError}
          testId="seed-file-error"
        />
      ) : null}
      {fileErrors.length > 0 ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title={`Nothing was sent: the file has ${plural(fileErrors.length, "error", "errors")}`}
          message={
            <div>
              <span>Fix these in the file, then choose it again.</span>
              <FindingList lines={fileErrors} testId="seed-file-findings" />
            </div>
          }
          testId="seed-file-error"
        />
      ) : null}
      {visibleWarnings.length > 0 ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title={plural(visibleWarnings.length, "warning", "warnings")}
          message={
            <div>
              <span>
                {fileErrors.length > 0 || serverFindings.length > 0
                  ? "Also worth fixing while you are in the file."
                  : "The file can still be applied. Check these first."}
              </span>
              <FindingList lines={visibleWarnings} testId="seed-warning-findings" />
            </div>
          }
          testId="seed-file-warnings"
        />
      ) : null}
    </>
  );
}

/** What applying the file would do: the classification, the drift and the uncompared blocks. */
function DryRunReport({
  dryRun,
  zone,
  nothingToApply,
  driftRows,
  columns,
}: {
  dryRun: DryRunState;
  zone: string;
  nothingToApply: boolean;
  driftRows: DriftRow[];
  columns: SkyDataGridColumn<DriftRow>[];
}) {
  return (
    <>
      <div>
        <SkyText as="h3" size={300} weight="semibold" block>
          {dryRunHeading(dryRun)}
        </SkyText>
        <SkyText size={200} color="muted" block>
          {dryRun.fileName} · uploaded{" "}
          {formatInstant(dryRun.uploadedAt, zone, "short", { named: true })} · checksum{" "}
          <span className="font-mono">{dryRun.result.checksum.slice(0, 12)}…</span>
        </SkyText>
      </div>

      {/* The bar the canvas insists comes first. */}
      <SkyMessageBar
        intent={nothingToApply ? "success" : "warning"}
        layout="multiline"
        title={nothingToApply ? "Nothing to apply" : "Nothing has been applied"}
        testId="drift-not-applied"
        message={
          nothingToApply
            ? "The stored definition already matches this file. Discard it."
            : "Review each difference, then apply the file or discard it. Applying writes the definition and is audited."
        }
      />

      {dryRun.result.action === "skip_drift" ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="The stored row has drifted since it was last seeded"
          testId="drift-classification"
          message={`${dryRun.result.detail}. Applying overwrites what is stored with what is in this file.`}
        />
      ) : null}

      {driftRows.length > 0 ? (
        <SkyDataGrid<DriftRow>
          data={driftRows}
          columns={columns}
          getRowId={(row) => row.id}
          testId="drift-grid"
        />
      ) : (
        <SkyText size={200} color="muted" block>
          No difference in the fields this screen can compare.
        </SkyText>
      )}

      {documentCarriesUncomparedBlocks(dryRun.seed) ? (
        <SkyText size={200} color="muted" block>
          The file also carries the seed's JSON blocks: field groups, field definitions, credential
          validation, entity selection and UI hints. They are not compared here. The checksum above
          covers them, which is why a file can differ with no row in the table.
        </SkyText>
      ) : null}
    </>
  );
}

export function SeedUploadDialog({
  open,
  onOpenChange,
  connectors,
  onClassify,
  onApply,
  isClassifying,
  isApplying,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectors: ConnectorAdminView[];
  onClassify: (seed: ConnectorSeedDocument) => Promise<ConnectorUpsertResult>;
  onApply: (seed: ConnectorSeedDocument, force: boolean) => Promise<ConnectorUpsertResult>;
  isClassifying: boolean;
  isApplying: boolean;
  error: unknown;
}) {
  const zone = useDisplayTz();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dryRun, setDryRun] = useState<DryRunState | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // What `validateSeed` found in the chosen file, before any request.
  const [fileErrors, setFileErrors] = useState<FindingLine[]>([]);
  const [fileWarnings, setFileWarnings] = useState<SeedWarning[]>([]);
  // The request error standing when a newer file was chosen belongs to the older file; it is
  // hidden until the caller's mutation answers again with its own (review r2).
  const [staleError, setStaleError] = useState<unknown>(null);
  const shownError = error && error !== staleError ? error : null;

  const stored = useMemo(
    () => connectors.find((connector) => connector.name === dryRun?.seed.name),
    [connectors, dryRun],
  );

  const driftRows = useMemo<DriftRow[]>(
    () => (dryRun ? seedDrift(stored, dryRun.seed) : []),
    [dryRun, stored],
  );

  function reset() {
    setDryRun(null);
    setFileError(null);
    setFileErrors([]);
    setFileWarnings([]);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function chooseFile(file: File) {
    setFileError(null);
    setFileErrors([]);
    setFileWarnings([]);
    setDryRun(null);
    setStaleError(error);
    let document: unknown;
    try {
      document = JSON.parse(await readTextFile(file));
    } catch {
      setFileError("That file is not valid JSON.");
      return;
    }

    const validation = validateSeed(document, { hubValidators: CONNECTOR_HUB_VALIDATORS });
    const warnings = seedWarnings(validation.findings).map(({ rule, field, issue }) => ({
      rule,
      field,
      issue,
    }));
    setFileWarnings(warnings);
    const errors = seedErrors(validation.findings);
    if (errors.length > 0 || validation.seed === null) {
      setFileErrors(errors.map(({ field, issue }) => ({ field, issue })));
      return;
    }
    const parsed: ConnectorSeedDocument = validation.seed;

    try {
      const result = await onClassify(parsed);
      setDryRun({
        seed: parsed,
        fileName: file.name,
        uploadedAt: new Date().toISOString(),
        result,
      });
    } catch {
      // The failure is rendered from `error` by the caller's mutation state.
      setDryRun(null);
    }
  }

  async function apply() {
    if (!dryRun) return;
    try {
      // `force` only when the row drifted: applying a clean update needs no
      // override, and passing one always would make the flag meaningless.
      await onApply(dryRun.seed, dryRun.result.action === "skip_drift");
      reset();
      onOpenChange(false);
    } catch {
      /* rendered from `error` */
    }
  }

  const columns: SkyDataGridColumn<DriftRow>[] = [
    {
      key: "kind",
      header: "Change",
      width: "14%",
      renderCell: (_value, row) => (
        // The word carries the meaning; the colour only repeats it, so the
        // table still reads correctly in greyscale.
        <SkyBadge appearance="tint" color={driftColor(row.kind)}>
          {row.kind}
        </SkyBadge>
      ),
    },
    {
      key: "field",
      header: "Field",
      width: "26%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="monospace" wrap>
          {row.field}
        </SkyText>
      ),
    },
    { key: "stored", header: "Stored", width: "30%" },
    { key: "inFile", header: "In the file", width: "30%" },
  ];

  const nothingToApply = dryRun?.result.action === "skip_no_changes";

  // The server's own warnings once it has answered (it runs the same validator, so they differ
  // only across a version skew), the browser's before.
  const visibleWarnings: FindingLine[] = dryRun?.result.warnings ?? fileWarnings;

  // A refusal the route answered (the same rules, run again server-side): its details carry
  // `seed.<path>`, listed under the error bar exactly as the browser's own findings are.
  const serverFindings = serverFindingsOf(shownError);

  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Upload a connector seed"
      description="The file is classified first. Nothing is written until you apply it."
      size="large"
      testId="seed-upload-dialog"
      // SkyDialog closes itself on a primary action, which would throw away a
      // form the server has just refused. The dialog closes when the mutation
      // succeeds, and only then; a failure leaves the typing where it is.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={
        dryRun && !nothingToApply
          ? { label: applyLabel(dryRun), onClick: () => void apply(), loading: isApplying }
          : undefined
      }
      secondaryAction={{
        label: dryRun ? "Discard file" : "Close",
        onClick: () => {
          reset();
          onOpenChange(false);
        },
      }}
    >
      <div className="flex flex-col gap-4">
        <SeedFindings
          shownError={shownError}
          fileError={fileError}
          fileErrors={fileErrors}
          serverFindings={serverFindings}
          visibleWarnings={visibleWarnings}
        />

        <div className="flex flex-col gap-2">
          <label className="sr-only" htmlFor="connector-seed-file">
            Connector seed document, as JSON
          </label>
          <input
            ref={fileInput}
            id="connector-seed-file"
            type="file"
            accept="application/json,.json"
            data-testid="seed-file-input"
            className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-secondary file:px-3 file:py-1.5 file:text-sm file:text-secondary-foreground"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void chooseFile(file);
            }}
          />
          <SkyText size={200} color="muted" block>
            The same V2 seed JSON `npm run seed:connectors` reads. Choosing a file classifies it
            against the stored definition. It does not write anything.
          </SkyText>
        </div>

        {isClassifying ? (
          <SkyText size={200} color="muted" block>
            Classifying the file…
          </SkyText>
        ) : null}

        {dryRun ? (
          <DryRunReport
            dryRun={dryRun}
            zone={zone}
            nothingToApply={nothingToApply}
            driftRows={driftRows}
            columns={columns}
          />
        ) : null}
      </div>
    </SkyDialog>
  );
}
